import React from 'react';
import { useQuery } from '@apollo/client/react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  ChampionBlock,
  LoadingPage,
  PageContainer,
  PlayoffBracket,
  ScheduleTable,
  StandingsBlock,
  Table,
} from 'Components';
import { GET_HOMEPAGE_DATA } from 'services/queries';
import {
  calculateWinStreak,
  createGoalieStatsLeague,
  createHomepageGoalieStats,
  createHomepageSkaterStats,
  createStandingsData,
  createUpcomingGames,
  CURRENT_SEASON,
  design,
} from 'utils';
import {
  getGoalieSingleSeasonHeadCells,
  getSkaterSingleSeasonHeadCells,
} from 'tableHeadCells';

const ScheduleLink = styled.p`
  font-size: ${design.fontSizes[1]};
  text-align: center;
`;
const SectionWrap = styled.section`
  margin-top: ${design.spacing[6]};
`;

const playerRowLinks = {
  firstName: {
    page: '/player',
    param: 'id',
  },
  lastName: {
    page: '/player',
    param: 'id',
  },
  team: {
    page: '/team',
    param: ['seasonId', 'teamId'],
  },
};

const HomePage = () => {
  const { loading, error, data } = useQuery(GET_HOMEPAGE_DATA, {
    variables: {
      seasonId: CURRENT_SEASON,
      seasonsArr: [CURRENT_SEASON - 1, CURRENT_SEASON],
    },
  });

  if (loading) return <LoadingPage />;
  if (error) return `error: ${error.message}`;

  const lastSeason = data.seasons.filter(
    (season) => season.id !== CURRENT_SEASON,
  )[0];
  const currentSeason = data.seasons.filter(
    (season) => season.id === CURRENT_SEASON,
  )[0];

  const showChampionAtTop = false;
  const championsBlockProps = {
    seasonId: lastSeason.id,
    seasonName: lastSeason.season,
    showImage: true,
    teamId: lastSeason.teams[0].id,
    teamName: lastSeason.teams[0].name,
    year: lastSeason.year,
  };
  // const championsBlockProps = {
  //   seasonId: currentSeason.id,
  //   seasonName: currentSeason.season,
  //   showImage: true,
  //   teamId: currentSeason.teams[0].id,
  //   teamName: currentSeason.teams[0].name,
  //   year: currentSeason.year,
  // };

  // Set up stat data
  const skaterData = createHomepageSkaterStats(data.teams);
  const goalieStats = createGoalieStatsLeague(data.schedule);
  const goalieData = createHomepageGoalieStats(goalieStats);

  // Set up Schedule
  const { gamesByWeek, isPlayoffs } = createUpcomingGames(data.schedule);

  // Set up Standings
  const standings = createStandingsData(
    [...data.schedule].filter((game) => !game.playoffs),
  );
  const teamStreaks = calculateWinStreak({
    schedule: [...data.schedule],
  });
  const showStandings = Object.keys(standings).length > 0;

  return (
    <PageContainer>
      {showChampionAtTop && (
        <div style={{ marginBottom: design.spacing[8] }}>
          <ChampionBlock {...championsBlockProps} />
        </div>
      )}
      <h1>
        {currentSeason.year} {currentSeason.season} Season
      </h1>
      {showStandings && !isPlayoffs && (
        <SectionWrap>
          <StandingsBlock
            seasonId={CURRENT_SEASON}
            standings={standings}
            teamStreaks={teamStreaks}
          />
        </SectionWrap>
      )}
      {Object.keys(gamesByWeek).length > 0 && (
        <>
          {isPlayoffs ? (
            <>
              <PlayoffBracket
                games={[
                  ...Object.values(gamesByWeek)[0],
                  ...Object.values(gamesByWeek)[1],
                ]}
              />
              <ScheduleLink>
                <Link to={`/schedule/${CURRENT_SEASON}`}>
                  View Season Schedule &raquo;
                </Link>
              </ScheduleLink>
            </>
          ) : (
            <SectionWrap>
              <h2>Recent Scores & Upcoming Games</h2>
              <ScheduleLink>
                <Link to={`/schedule/${CURRENT_SEASON}`}>
                  View Complete Schedule &raquo;
                </Link>
              </ScheduleLink>
              <ScheduleTable
                gamesByWeek={gamesByWeek}
                seasonId={CURRENT_SEASON}
              />
            </SectionWrap>
          )}
        </>
      )}
      {showStandings && isPlayoffs && (
        <SectionWrap>
          <StandingsBlock
            seasonId={CURRENT_SEASON}
            standings={standings}
            teamStreaks={teamStreaks}
          />
        </SectionWrap>
      )}
      {skaterData.length > 0 && (
        <SectionWrap>
          <h2>Player Leaders</h2>
          <Table
            defaultOrder="desc"
            defaultOrderBy="points"
            enablePagination
            headCells={getSkaterSingleSeasonHeadCells({
              showJersey: true,
              showTeam: true,
            })}
            rowLinks={playerRowLinks}
            rows={skaterData}
            size="small"
            tableTitle="Leaders"
          />
        </SectionWrap>
      )}
      {goalieData.length > 0 && (
        <SectionWrap>
          <h2>Goalie Leaders</h2>
          <Table
            defaultOrder="desc"
            defaultOrderBy="svp"
            headCells={getGoalieSingleSeasonHeadCells({
              showAssists: true,
              showJersey: true,
              showPim: true,
              showTeam: true,
            })}
            rowLinks={playerRowLinks}
            rows={[...new Set(goalieData)]}
            size="small"
            tableTitle="Leaders"
          />
        </SectionWrap>
      )}
      {!showChampionAtTop && <ChampionBlock {...championsBlockProps} />}
    </PageContainer>
  );
};

export default HomePage;
